import emptyApi from '@epi-api/emptyApi';
import { get, post, put } from '@epi-helpers/FetchHelpers';
import {
  IBeneficiaryData,
  IBeneficiaryPayload
} from '@epi-models/pages/Beneficiaries';
import {
  BeneficiariesResponse,
  CompanyBeneficiariesSsnResponse
} from '@epi-models/pages/Transfer';

import config from '@epi-config';
import { IActionResponse } from '@epi-api/actionResponse';
import { BENEFICIARY_TAGS, USER_TAGS } from '@epi-api/apiTags';

const url = `${config.apiHost}/api/company-beneficiaries`;

export function getCompanyBeneficariesRequest(
  abortController: AbortController | null = null
): Promise<BeneficiariesResponse> {
  return get(url, undefined, undefined, abortController?.signal);
}

export const getCompanyBeneficariesSsn = async (
  beneficiaryPersonId: number,
  signal: AbortSignal
): Promise<{ data?: CompanyBeneficiariesSsnResponse; error?: any }> => {
  try {
    const response: CompanyBeneficiariesSsnResponse | undefined = await get(
      `${url}/${beneficiaryPersonId}/ssn`,
      undefined,
      undefined,
      signal
    );
    return { data: response };
  } catch (e) {
    return { error: e };
  }
};

export const createCompanyBeneficiary = async (
  payload: IBeneficiaryPayload
): Promise<{ data?: IActionResponse; error?: any }> => {
  try {
    const response = await post(url, payload);
    return { data: response ?? {} };
  } catch (e) {
    return { error: e };
  }
};

export const getCompanyBeneficiaries = async (
  signal: AbortSignal
): Promise<{
  data?: BeneficiariesResponse;
  error?: any;
}> => {
  try {
    const response: BeneficiariesResponse | undefined = await get(url, signal);
    return { data: response };
  } catch (e) {
    return { error: e };
  }
};

export const checkSSN = async (
  personalId: string
): Promise<{ data?: boolean; error?: any }> => {
  try {
    const response: boolean | undefined = await post(`${url}/check-SSN`, {
      personalId
    });
    return { data: response };
  } catch (e: any) {
    return {
      error: {
        status: e.status ?? 400,
        data: e?.content ?? {}
      }
    };
  }
};

export const updateCompanyBeneficiary = async (
  payload: IBeneficiaryPayload
): Promise<{ data?: IActionResponse; error?: any }> => {
  try {
    const response = await put(url, payload);
    return { data: response ?? {} };
  } catch (e: any) {
    return { error: e };
  }
};

export const getBeneficiaryDetails = async (
  personalId: string,
  signal: AbortSignal
): Promise<{
  data?: IBeneficiaryData;
  error?: any;
}> => {
  try {
    const response: IBeneficiaryData | undefined = await get(
      `${url}/${personalId}`,
      signal
    );
    return { data: response };
  } catch (e) {
    return { error: e };
  }
};

export const companyBeneficiariesApi = emptyApi.injectEndpoints({
  endpoints: build => ({
    getCompanyBeneficiariesSSN: build.query<
      CompanyBeneficiariesSsnResponse,
      number
    >({
      queryFn: async (personalId, { signal }) =>
        getCompanyBeneficariesSsn(personalId, signal),
      keepUnusedDataFor: 1800
    }),
    getCompanyBeneficiaries: build.query<BeneficiariesResponse, void>({
      queryFn: async (_, { signal }) => getCompanyBeneficiaries(signal),
      keepUnusedDataFor: 1800,
      providesTags: ['companyBeneficiariesList']
    }),
    createCompanyBeneficiary: build.mutation<
      IActionResponse,
      IBeneficiaryPayload
    >({
      queryFn: createCompanyBeneficiary,
      invalidatesTags: [...BENEFICIARY_TAGS, ...USER_TAGS]
    }),
    updateCompanyBeneficiary: build.mutation<
      IActionResponse,
      IBeneficiaryPayload
    >({
      queryFn: updateCompanyBeneficiary,
      invalidatesTags: [...BENEFICIARY_TAGS, ...USER_TAGS]
    }),
    checkCompanyBeneficiarySSN: build.mutation<boolean, string>({
      queryFn: checkSSN
    }),
    getBeneficiaryDetails: build.query<IBeneficiaryData, string>({
      queryFn: async (personalId, { signal }) =>
        getBeneficiaryDetails(personalId, signal),
      keepUnusedDataFor: 1800,
      providesTags: ['beneficiaryDetails']
    })
  })
});

export const {
  useGetCompanyBeneficiariesSSNQuery,
  useLazyGetCompanyBeneficiariesSSNQuery,
  useGetCompanyBeneficiariesQuery,
  useLazyGetCompanyBeneficiariesQuery,
  useCreateCompanyBeneficiaryMutation,
  useCheckCompanyBeneficiarySSNMutation,
  useUpdateCompanyBeneficiaryMutation,
  useGetBeneficiaryDetailsQuery,
  useLazyGetBeneficiaryDetailsQuery
} = companyBeneficiariesApi;
